import { Fragment, useCallback, useState } from "react"
import { toast } from "react-toastify"

import { paymentClient, useQueryClient } from "@trueskin-web/apis"
import { Button, Icon } from "@trueskin-web/components"
import { trackingService } from "@trueskin-web/services"
import { lockIcon } from "@trueskin-web/theme/icons/lockIcon"
import { Trans, useTranslation } from "@trueskin-web/translations"

import { usePaymentContext } from "../../payment.context"

import PaymentFailDialog from "./payment-fail-dialog"

const RepurchaseButton = ({ primaryPaymentMethod, order }) => {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false)
  const [showPaymentFailDialog, setShowPaymentFailDialog] = useState(false)
  const { setAddNewPaymentMethod } = usePaymentContext()

  const handleError = useCallback(
    ({ data: { refusalReasonCode } = {} } = {}) => {
      setShowPaymentFailDialog(true)

      toast(
        t(
          `Payment.errors.${refusalReasonCode}`,
          t("Payment.errors.UNKNOWN_REFUSAL_REASON")
        )
      )
    },
    [t]
  )

  const handleSubmit = useCallback(async () => {
    const marketingEventData = {
      amount: order.amount,
      currency: order.currency,
      productType: order.productType,
      voucherCode: order.voucherCode,
    }
    const paymentDetails = {
      orderId: order.orderId,
      provider: "stored_payment_method",
      paymentMethodId: primaryPaymentMethod.paymentMethodId,
      gateway: primaryPaymentMethod.gateway,
      token: primaryPaymentMethod.token,
      v2CustomerId: primaryPaymentMethod.v2CustomerId,
    }

    setIsPaymentProcessing(true)

    return paymentClient
      .purchaseOrder(
        trackingService.getTrackingTouchpoints(),
        marketingEventData,
        paymentDetails
      )
      .then(() => {
        queryClient.refetchQueries("currentUser")
      })
      .catch(handleError)
      .finally(() => {
        setIsPaymentProcessing(false)
      })
  }, [order, primaryPaymentMethod, handleError, queryClient])

  return (
    <Fragment>
      <Button
        data-testid="web-portal-product-summary-repurchase-btn"
        onClick={handleSubmit}
        disabled={isPaymentProcessing}
        isLoading={isPaymentProcessing}
        isFullWidth
        sx={{
          mt: [2, 5],
        }}
      >
        <Icon icon={lockIcon} size="sm" sx={{ mr: 2 }} />
        <Trans i18nKey="Button.pay" />
      </Button>

      {showPaymentFailDialog && (
        <PaymentFailDialog
          onDismiss={() => {
            setShowPaymentFailDialog(false)
          }}
          onClick={() => {
            setShowPaymentFailDialog(false)
            setAddNewPaymentMethod(true)
          }}
          noBackdropDismiss
          noMobileFullscreen
        />
      )}
    </Fragment>
  )
}

export default RepurchaseButton
